import type { GeneralTimezone, TruentityDateInput } from '@/shared/lib/types/date';
import { toTimezoneDate } from '@/shared/lib/util/date';
import type { MomentInput } from 'moment';
import moment from 'moment';
import { emptyString } from './string';

export function formatDate(date: MomentInput, format = 'MMM DD, YYYY') {
  if (!date) {
    return '';
  }
  return moment(date).format(format);
}

export function formatToUTCDate(date: MomentInput, format = 'MMM DD, YYYY') {
  if (!date) {
    return '';
  }
  return moment.utc(date).format(format);
}

export function formatDateIgnoreTZ(date: MomentInput, format = 'MMM DD, YYYY') {
  if (!date) {
    return '';
  }
  return moment.utc(date).format(format);
}

export function formatYears(date: MomentInput, unit = 'y') {
  if (!date) {
    return '';
  }
  return `${moment().diff(moment.utc(date), 'years')}${unit}`;
}

export function formatDateTimeIgnoreTZ(date: MomentInput, format = 'MMM DD, YYYY (h:mm:ss A)') {
  if (!date) {
    return '';
  }
  return moment.utc(date).format(format);
}

export function formatDateIgnoreTZAddDays(date: MomentInput, days: number, format = 'MMM DD, YYYY') {
  if (!date) {
    return '';
  }
  return moment.utc(date).add(days, 'days').format(format);
}

export function formatDateIgnoreTZRemoveDays(date: MomentInput, days: number, format = 'MMM DD, YYYY') {
  if (!date) {
    return '';
  }
  return moment.utc(date).subtract(days, 'days').format(format);
}

export function addMinutes(date: MomentInput, minutes: number) {
  return moment(date).add('minutes', minutes).toDate();
}

export function removeMinutes(date: MomentInput, minutes: number) {
  return moment(date).subtract('minutes', minutes);
}

export function formatDateAndTime(date: MomentInput, timezoneText?: string | null, format = 'MMM DD, YYYY (h:mm A)') {
  if (!date) {
    return '';
  }
  const formatted = moment(date).format(format);
  return timezoneText ? `${formatted} ${timezoneText}` : formatted;
}

export function formatDateAndTime2(date: TruentityDateInput, time: TruentityDateInput, format = 'YYYY-MM-DD HH:mm') {
  if (date && time) {
    return moment(`${formatDate(date, 'YYYY-MM-DD')} ${time.toString()}`, format).format();
  }
  return emptyString();
}

export const formatDateAndTimeMoments = (date: TruentityDateInput, time: TruentityDateInput, format = 'YYYY-MM-DD HH:mm:ss') => {
  if (date && time) {
    return moment(`${formatDate(date, 'YYYY-MM-DD')} ${formatDate(time, 'HH:mm:ss')}`, format).format();
  }
};

export function formatTime(date: MomentInput, format = 'h:mm A', timezoneText?: string | null) {
  const formatted = moment(date).format(format);
  return timezoneText ? `${formatted} ${timezoneText}` : formatted;
}

export function duration(seconds: number | undefined) {
  const duration = moment.duration(seconds, 'seconds');

  if (seconds && seconds > 3600) {
    return (
      Math.floor(moment.duration(seconds, 'seconds').asHours()).toString() +
      ':' +
      moment.duration(seconds, 'seconds').minutes().toString() +
      ':' +
      moment.duration(seconds, 'seconds').seconds().toString() +
      ' hours'
    );
  }

  if (seconds && seconds > 60) {
    return (
      moment.duration(seconds, 'seconds').minutes().toString() + ':' + moment.duration(seconds, 'seconds').seconds().toString() + ' minutes'
    );
  }

  return duration.seconds().toString() + ' seconds';
}

export function secondsToTime(seconds: number) {
  return moment.utc(seconds * 1000).format('HH:mm:ss');
}

export function secondsToMinutes(seconds: number): string {
  return moment.utc(seconds * 1000).format('mm:ss');
}

export function toLocalDate(date: MomentInput, resetTime = false) {
  let localDate = moment(date).local();
  if (resetTime) {
    localDate = resetDateTime(localDate);
  }
  return localDate.toDate();
}

export function resetDateTime(date: MomentInput) {
  return moment(date).hours(0).minutes(0).seconds(0);
}

export function formatSecondsToHMS(seconds: number) {
  const duration = moment.duration(seconds, 'seconds');
  const hours: number = duration.hours();
  const minutes: number = duration.minutes();
  const remainingSeconds: number = duration.seconds();

  const formattedTime: string[] = [];

  if (hours > 0) {
    formattedTime.push(`${hours}h`);
  }

  if (minutes > 0) {
    formattedTime.push(`${minutes}m`);
  }

  if (remainingSeconds > 0 || formattedTime.length === 0) {
    formattedTime.push(`${remainingSeconds}s`);
  }

  return formattedTime.join(' ');
}

export function formatMsToDaysString(milliseconds: string) {
  const duration = moment.duration(milliseconds);
  const hours = duration.hours();
  const minutes = duration.minutes();

  const parts: string[] = [];

  if (hours > 0) {
    parts.push(`${hours} hour${hours > 1 ? 's' : ''}`);
  }
  if (minutes > 0) {
    parts.push(`${minutes} minute${minutes > 1 ? 's' : ''}`);
  }

  return parts.join(' ');
}

export function minutesToSeconds(minutes: number | null) {
  return moment.duration(minutes, 'minutes').asSeconds();
}

export function formatTimeToISOString(time: string) {
  const currentDate = moment().format('YYYY-MM-DD');
  const [minutes, seconds] = time.split(':');
  const currentHour = moment().format('HH'); // Get current hour in 24-hour format

  // Use Moment.js to format the date-time with the provided minutes, current hour, and seconds
  const formattedDateTime = moment(`${currentDate}T${currentHour}:${minutes}:${seconds}Z`).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');

  return formattedDateTime;
}

export function getCurrentDate() {
  return moment();
}

export function startOfDay(date: moment.Moment) {
  return date.startOf('day');
}

export function endOfDay(date: moment.Moment) {
  return date.endOf('day');
}

export function isSameOrAfter(date: MomentInput, compareTo: MomentInput) {
  return moment(date).isSameOrAfter(compareTo, 'day');
}

export function isSame(date: MomentInput, compareTo: MomentInput) {
  return moment(date).isSame(compareTo, 'day');
}

export function isBefore(date: MomentInput, compareTo: MomentInput) {
  return moment(date).isBefore(compareTo, 'day');
}

export function titleCase(string: string) {
  return string[0].toUpperCase() + string.slice(1).toLowerCase();
}

export function setSpecificTimeOnDate(hours: number, minutes: number, seconds: number) {
  return moment({ hour: hours, minute: minutes, seconds: seconds }).format('HH:mm');
}

export function checkIfCurrentMonth(startDate: MomentInput) {
  const startDateMoment = moment(startDate);
  return moment().isSame(startDateMoment, 'month');
}

type FormatDateToTimezoneInput = {
  date: MomentInput;
  timezone: GeneralTimezone;
  format?: string;
};

export function formatDateToTimezone({ date, timezone, format = 'MMM DD, YYYY' }: FormatDateToTimezoneInput) {
  try {
    const momentDate = toTimezoneDate({ date, timezone });
    return momentDate.format(format);
  } catch (error) {
    return '';
  }
}

/** A phone number's 10 national digits, so "+1 (269) 815-9806" and "2698159806" compare equal. */
export const phoneNumberDigits = (input?: string | null) => (input ?? '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');

export const formatPhoneNumber = (input: string) => {
  const cleaned = input?.replace(/\D/g, '')?.replace(/^1/, '');
  if (cleaned.length === 10) {
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    return match ? `(${match[1]}) ${match[2]}-${match[3]}` : null;
  }
  return null;
};

export const formatOnlyDateTime = (date: MomentInput, format = 'MMM DD, YYYY') => {
  return moment(date).clone().utcOffset(0, true).format(format);
};

export const formatEndOfDay = (date: MomentInput) => {
  return moment(date).endOf('day').utc().format('YYYY-MM-DDTHH:mm:ss[z]');
};

export const getSameDayOfPreviousMonth = (monthsAgo: number): string => {
  const date = moment().subtract(monthsAgo, 'months');
  return date.format('YYYY-MM-DD');
};

/**
 * Format date as a compact date string (MMDDYYYY) without separators
 * @param date - JavaScript Date object (defaults to current date if not provided)
 * @returns string in MMDDYYYY format
 */
export const formatCompactDate = (date: Date = new Date()): string => {
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}${day}${year}`;
};

export function formatSecondsToShortHMS(totalSeconds: number) {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const parts: string[] = [];

  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);

  return parts.join(' ');
}
